import { differenceInDays, format, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_PARAM_NAMES = { start: 'date_start', end: 'date_end' };

function parseDateParam(value) {
  if (!value) return null;
  return typeof value === 'string' ? parseISO(value) : value;
}

/**
 * Read/write a start/end date range that lives in the URL search params.
 *
 * Mirrors the report-view pattern used across the power/measurement views:
 * dates are stored as `yyyy-MM-dd` strings, missing/invalid params fall
 * back to the supplied defaults, and `onDateRangeChange` only navigates
 * when the incoming range actually differs (day granularity) from the
 * current one.
 *
 * @param {Object} options
 * @param {Date} options.defaultStart  used when the start param is absent/invalid
 * @param {Date} options.defaultEnd    used when the end param is absent/invalid
 * @param {{start: string, end: string}} [options.paramNames]  search-param keys
 * @returns {{ startDate: Date, endDate: Date, onDateRangeChange: (start: Date, end: Date) => void }}
 */
export default function useDateRangeSearchParams({ defaultStart, defaultEnd, paramNames = DEFAULT_PARAM_NAMES }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const startDate = useMemo(
    () => parseDateParam(searchParams.get(paramNames.start)) || defaultStart,
    [searchParams, paramNames.start, defaultStart],
  );
  const endDate = useMemo(
    () => parseDateParam(searchParams.get(paramNames.end)) || defaultEnd,
    [searchParams, paramNames.end, defaultEnd],
  );

  const onDateRangeChange = useCallback(
    (start, end) => {
      if (differenceInDays(start, startDate) !== 0 || differenceInDays(end, endDate) !== 0) {
        const next = new URLSearchParams(location.search);
        next.set(paramNames.start, format(start, DATE_FORMAT));
        next.set(paramNames.end, format(end, DATE_FORMAT));
        navigate({ pathname: location.pathname, search: next.toString() });
      }
    },
    [startDate, endDate, location, navigate, paramNames.start, paramNames.end],
  );

  return { startDate, endDate, onDateRangeChange };
}
